import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import Api from "app/helpers/api";
import { DatatableComponentComponent } from "app/modules/acompanhamento-execucoes/datatable-component/datatable-component.component";
import introJs from "intro.js";
import { IntroStep } from "intro.js/src/core/steps";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, interval } from "rxjs";
import { debounce } from "rxjs/operators";
import { TableExpansionService } from "../treinos/treinos-edit/services";
import {
  ExecutionListFilters,
  ExecutionListRequestType,
  ExecutionListResponseType,
  RelationsListResponseType,
} from "./acompanhamento-execucoes.types";

const TOUR_KEY = "is_tour_performed:executions_monitoring";

const initialFilters: ExecutionListFilters = {
  lightFeedback: false,
  heavyFeedback: false,
  withObservation: false,
  withLoadNotification: false,
};

@Component({
  selector: "app-acompanhamento-execucoes",
  templateUrl: "./acompanhamento-execucoes.component.html",
  styleUrls: ["./acompanhamento-execucoes.component.scss"],
  providers: [TableExpansionService],
})
export class AcompanhamentoExecucoesComponent implements OnInit, AfterViewInit {
  apiCall = this.api.new().silent();
  introJS = introJs();

  $executions = new BehaviorSubject<ExecutionListResponseType["executions"]>(
    []
  );

  $relations = new BehaviorSubject<RelationsListResponseType | null>(null);

  $isLoading = new BehaviorSubject<boolean>(false);
  $isFetching = new BehaviorSubject<boolean>(false);

  $shouldFetchNextPage = new BehaviorSubject<boolean>(true);

  hasFeedback = false;
  feedback: "negative" | "neutral" | "positive" | null = null;
  feedbackNote: string = "";
  feedbackLoading: boolean = false;

  // pagination
  offset = 0;
  limit = 20;

  $filters = new BehaviorSubject<ExecutionListFilters>(initialFilters);
  filters: string[] = [];

  $activeTabId = new BehaviorSubject<"all" | "importants">("all");
  set activeTabId(value: "all" | "importants") {
    this.$activeTabId.next(value);
  }
  get activeTabId(): "all" | "importants" {
    return this.$activeTabId.value;
  }
  isImportant = false;

  $search = new BehaviorSubject<string>("");
  set search(value: string) {
    this.$search.next(value);
  }
  get search(): string {
    return this.$search.value;
  }

  @ViewChild("datatableAll") datatableAll?: DatatableComponentComponent;
  @ViewChild("datatableImportants")
  datatableImportants?: DatatableComponentComponent;

  isOnTour = false;

  constructor(
    private tableExpansionService: TableExpansionService,
    private api: Api,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private ref: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit() {
    this.$isLoading.next(true);
    this.loadindLifeCycleManager();
    this.initializeSearch();
    this.initializeTabChange();

    this.fetchEvolutions();

    this.apiCall
      .get<RelationsListResponseType>("sellercustomerplataforma")
      .subscribe((response) => {
        if (response.return) {
          this.$relations.next(response.return);
        }
      });

    const isTourPerformed = localStorage.getItem(TOUR_KEY);

    if (isTourPerformed === "true") {
      this.isOnTour = false;
    } else {
      this.isOnTour = true;
    }
  }

  ngAfterViewInit() {
    this.initializeChildsInjections();
    this.initializeIntroJs();
  }

  fetchEvolutions(fetchNextPage = false) {
    if (this.$shouldFetchNextPage.value === false) return;

    if (fetchNextPage) {
      this.offset = this.offset + this.limit;
    } else {
      this.$executions.next([]);
    }

    this.$isFetching.next(true);

    const filters = this.$filters.value;

    this.apiCall
      .get<ExecutionListResponseType, ExecutionListRequestType>(
        "training/evolution",
        {
          offset: this.offset,
          limit: this.limit,
          search: this.search,
          isImportant: this.isImportant,
          ...filters,
        }
      )
      .subscribe((response) => {
        if (response.error) {
          this.toastr.error(
            "Ocorreu um erro ao buscar as evoluções, tente novamente."
          );
        } else if (response.return) {
          if (response.return.executions.length < 20) {
            this.$shouldFetchNextPage.next(false);
          }

          this.$executions.next([
            ...this.$executions.value,
            ...response.return.executions,
          ]);

          this.hasFeedback = !!response.return.feedback;
        }

        this.$isFetching.next(false);

        if (this.$isLoading.value) {
          this.$isLoading.next(false);

          if (this.isImportant) {
            this.datatableImportants?.initializeInfiniteScroll();
          } else {
            this.datatableAll?.initializeInfiniteScroll();
          }
        }
      });
  }

  refetchEvolutions() {
    this.offset = 0;
    this.tableExpansionService.reset();
    this.$isLoading.next(true);
    this.$shouldFetchNextPage.next(true);
    this.fetchEvolutions();
  }

  initializeSearch() {
    this.$search.pipe(debounce(() => interval(300))).subscribe(() => {
      if (this.$isLoading.value) return;

      this.refetchEvolutions();
    });
  }

  initializeTabChange() {
    this.$activeTabId.subscribe((tabId) => {
      if (this.$isLoading.value) return;

      this.isImportant = tabId === "importants";
      this.refetchEvolutions();
    });
  }

  initializeChildsInjections() {
    this.$executions.subscribe((executions) => {
      if (this.isImportant && this.datatableImportants) {
        this.datatableImportants.executions = executions;
      } else if (this.datatableAll) {
        this.datatableAll.executions = executions;
      }
      this.ref.detectChanges();
    });

    this.$shouldFetchNextPage.subscribe((shouldFetchNextPage) => {
      if (!shouldFetchNextPage) {
        if (this.isImportant && this.datatableImportants) {
          this.datatableImportants.removeScrollListener();
        } else if (this.datatableAll) {
          this.datatableAll.removeScrollListener();
        }
      }
    });
  }

  initializeIntroJs() {
    const self = this;

    const total = 7;

    const getStepTitleWithCurrentStep = (current: number) =>
      `<div class='w-100 text-center'><span class='d-block grey darken-1 font-size-3 font-size-md-4 text-bold-400'>Passo <b> ${current} </b> de <b> ${total} </b></span></div>`;

    $(document).ready(function () {
      const checkExist = setInterval(function () {
        if (self.$executions.value.length > 0) {
          const steps: Partial<IntroStep>[] = [
            {
              title: getStepTitleWithCurrentStep(1),
              intro:
                "<div class='w-100 text-center'><span class='d-block'>Olá, bem vindo à <b> tela de acompanhamento! </b></span><br /><span class='d-block'>Vou te mostrar rapidamente como analisar o <b> histórico de treinos </b> de seus alunos.</span></div>",
              scrollTo: "tooltip",
            },
            {
              element: "#tour-step__2-3",
              title: getStepTitleWithCurrentStep(2),
              intro:
                "<div class='w-100 text-center'><span class='d-block'>Na aba <b> “Todos” </b>, você tem acesso ao histórico completo de todos seus alunos, em <b> ordem cronológica. </b></span></div>",
              disableInteraction: true,
              scrollTo: "tooltip",
            },
            {
              element: "#tour-step__2-3",
              title: getStepTitleWithCurrentStep(3),
              intro:
                "<div class='w-100 text-center'><span class='d-block'>Para ver os treinos que necessitam atenção, clique na aba <b> “Mais Importantes”. </b></span><br /><span class='d-block'>Aqui, você vê os treinos que possuem: <b> comentários </b> e/ou <b> variação de carga </b> e/ou com feedback <b> leve/pesado. </b></span></div>",
              disableInteraction: true,
              scrollTo: "tooltip",
            },
          ];

          const filteredSteps = steps.filter((obj) => {
            let isElementVisible = true;

            if (obj && obj.element != null) {
              if ($(obj.element as string).is(":visible") === false) {
                isElementVisible = false;
              }
            }
            return isElementVisible;
          });

          self.introJS.setOptions({
            tooltipClass: "customTooltip",
            nextLabel: "Próximo",
            doneLabel: "Concluir!",
            prevLabel: "Voltar",
            hideNext: true,
            hidePrev: true,
            exitOnOverlayClick: false,
            exitOnEsc: false,
            overlayOpacity: 0.2,
            steps: filteredSteps,
            showBullets: false,
            showProgress: false,
          });

          if (self.introJS && self.isOnTour) {
            self.introJS.start();

            $(".execution-datatable").css({
              overflow: "hidden",
            });
          }

          clearInterval(checkExist);
        }
      }, 100);
    });

    if (this.introJS) {
      this.introJS.onbeforechange(async function () {
        $(document).ready(() => {
          if (this._currentStep === 2) {
            $(".introjs-prevbutton").hide();
          }

          $(".introjs-skipbutton").css({ float: "left" });
          $(".introjs-nextbutton").css({
            background: "#40C057",
            color: "white",
            "text-shadow": "none",
            border: "1px solid rgb(58 173 79)",
          });
        });

        if (this._currentStep === 2) {
          if (self.activeTabId === "all") {
            self.activeTabId = "importants";
            $(".execution-datatable").css({
              overflow: "hidden",
            });
          }

          if (self.$isLoading.value) {
            await new Promise((resolve) => {
              const subscription = self.$isLoading.subscribe((isLoading) => {
                if (!isLoading) {
                  subscription.unsubscribe();
                  resolve(true);
                }
              });
            });
          }
        }

        if (this._currentStep === 3) {
          const row = document.getElementById("tour-step__4__clickable-item");

          if (row) {
            if (Array.from(self.tableExpansionService.expanded).length === 0) {
              row.click();
              await new Promise((resolve) => setTimeout(resolve, 600));

              this.refresh(true);
            }
          }
        }

        if (this._currentStep === 4) {
          const row = document.getElementById("tour-step__5__clickable-item");

          if (row) {
            row.click();
            await new Promise((resolve) => setTimeout(resolve, 200));

            this.refresh(true);
          }
        }

        if (this._currentStep === 5) {
          const row = document.getElementById("tour-step__6__clickable-item");

          if (row) {
            row.click();
            await new Promise((resolve) => setTimeout(resolve, 200));

            this.refresh(true);
          }
        }

        if (this._currentStep === 6) {
          const nextBtn = document.querySelector(
            ".introjs-nextbutton"
          ) as HTMLElement;

          if (nextBtn) {
            nextBtn.innerHTML = "Concluir!";

            nextBtn.onclick = () => {
              this.exit(false);
            };
          }
        }

        return true;
      });

      this.introJS.onafterchange(function () {
        $(document).ready(() => {
          $(".introjs-nextbutton").show();
        });

        if (this._currentStep === 2) {
          $("#tour-step__4").ready(() => {
            this.addSteps([
              {
                element: "#tour-step__4",
                title: getStepTitleWithCurrentStep(4),
                intro:
                  "<div class='w-100 text-center'><span class='d-block'>Ao clicar em algum treino, você poderá ver mais detalhes.</span><br><span class='d-block'>Aqui, você pode ver as <b> observações (<svg id='comment-ballon' width='16' height='16' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg' class='d-inline'><path d='M2.5207 3.15508C2.27927 3.15508 2.08355 3.3508 2.08355 3.59223C2.08355 3.83366 2.27927 4.02938 2.5207 4.02938V3.15508ZM6.60077 4.02938C6.8422 4.02938 7.03792 3.83366 7.03792 3.59223C7.03792 3.3508 6.8422 3.15508 6.60077 3.15508V4.02938ZM3.03071 4.64663C2.78928 4.64663 2.59356 4.84236 2.59356 5.08378C2.59356 5.32521 2.78928 5.52093 3.03071 5.52093V4.64663ZM6.09076 5.52093C6.33219 5.52093 6.52791 5.32521 6.52791 5.08378C6.52791 4.84236 6.33219 4.64663 6.09076 4.64663V5.52093ZM2.61571 1.10513V0.667969L2.61252 0.667992L2.61571 1.10513ZM6.50635 1.10513L6.50967 0.66798H6.50635V1.10513ZM8.64081 3.27165L8.20366 3.26844V3.27165H8.64081ZM8.64081 5.90213H8.20366V5.90533L8.64081 5.90213ZM6.50576 8.06865V8.5058H6.50897L6.50576 8.06865ZM2.61571 8.06865V7.6315C2.54086 7.6315 2.46727 7.65073 2.40198 7.68734L2.61571 8.06865ZM0.480666 9.26527H0.0435153C0.0435153 9.42043 0.125787 9.56399 0.259683 9.64245C0.393573 9.7209 0.559026 9.72247 0.694397 9.64658L0.480666 9.26527ZM0.480666 3.27165H0.917828L0.917804 3.26844L0.480666 3.27165ZM2.5207 4.02938H6.60077V3.15508H2.5207V4.02938ZM3.03071 5.52093H6.09076V4.64663H3.03071V5.52093ZM2.61571 1.54228H6.50635V0.66798L2.61571 0.667969V1.54228ZM6.50303 1.54227C7.44931 1.54942 8.21059 2.32219 8.20366 3.26844L9.07796 3.27486C9.08845 1.84584 7.93869 0.678787 6.50967 0.66798L6.50303 1.54227ZM8.20366 3.27165V5.90213H9.07796V3.27165H8.20366ZM8.20366 5.90533C8.20698 6.35985 8.02967 6.797 7.71061 7.12073L8.33335 7.73443C8.81514 7.24552 9.08297 6.58531 9.07796 5.89892L8.20366 5.90533ZM7.71061 7.12073C7.39161 7.44445 6.95708 7.62817 6.50256 7.6315L6.50897 8.5058C7.1953 8.50078 7.85161 8.22334 8.33335 7.73443L7.71061 7.12073ZM6.50576 7.6315H2.61571V8.5058H6.50576V7.6315ZM2.40198 7.68734L0.266934 8.88396L0.694397 9.64658L2.82944 8.44996L2.40198 7.68734ZM0.917816 9.26527L0.917828 3.27165H0.0435153V9.26527H0.917816ZM0.917804 3.26844C0.914465 2.81395 1.09181 2.37675 1.41083 2.05303L0.788105 1.43935C0.306319 1.92823 0.0384851 2.58849 0.0435269 3.27486L0.917804 3.26844ZM1.41083 2.05303C1.72986 1.72931 2.16441 1.54558 2.6189 1.54227L2.61252 0.667992C1.92615 0.672993 1.26989 0.950461 0.788105 1.43935L1.41083 2.05303Z' fill='currentColor' /></svg>) </b> deixadas pelos seus alunos, a <b> carga utilizada </b>, se houve <b> variação de carga (<svg width='8' height='10' viewBox='0 0 8 10' fill='none' xmlns='http://www.w3.org/2000/svg' class='d-inline'><path d='M3.80042 1V9.32617M3.80042 1L1 3.96191M3.80042 1L6.61603 3.96191' stroke='#83C05F' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round' /></svg><svg width='8' height='10' viewBox='0 0 8 10' fill='none' xmlns='http://www.w3.org/2000/svg' class='d-inline'><path d='M3.81561 9.32617L3.81561 1M3.81561 9.32617L6.61603 6.36426M3.81561 9.32617L1 6.36426' stroke=#E0625A stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round' /></svg>) </b> e também a <b> evolução de carga (<svg width='12' height='11' viewBox='0 0 12 11' fill='none' xmlns='http://www.w3.org/2000/svg' class='d-inline'><path d='M11 10H1.88889C1.57775 10 1.42218 10 1.30334 9.9346C1.19881 9.87706 1.11382 9.78532 1.06055 9.6724C1 9.54406 1 9.37605 1 9.03999V1M11 1.59995L7.98094 4.86055C7.87094 4.97935 7.81594 5.03875 7.7525 5.06101C7.69672 5.08063 7.63661 5.08063 7.58083 5.06101C7.51739 5.03875 7.46239 4.97935 7.35239 4.86055L6.31428 3.73938C6.20428 3.62058 6.14928 3.56118 6.08583 3.53892C6.03006 3.5193 5.96994 3.5193 5.91417 3.53892C5.85072 3.56118 5.79572 3.62058 5.68572 3.73938L3 7.84004M11 1.59995H8.77778M11 1.59995V3.99996' stroke='#F77E17' stroke-width='1.30435' stroke-linecap='round' stroke-linejoin='round' /></svg>) </b> no exercício em questão.</span></div>",
                disableInteraction: true,
                scrollTo: "tooltip",
              },
              {
                element: "#tour-step__5",
                title: getStepTitleWithCurrentStep(5),
                intro:
                  "<div class='w-100 text-center'><span class='d-block'>Caso necessário, você também pode <b> filtrar </b> os treinos, selecionando aspectos específicos que deseja analisar.</span></div>",
                disableInteraction: true,
                scrollTo: "tooltip",
              },
              {
                element: "#tour-step__6",
                title: getStepTitleWithCurrentStep(6),
                intro:
                  "<div class='w-100 text-center'><span class='d-block'>Por fim, clicando no menu (<svg width='3' height='10' viewBox='0 0 3 10' fill='none' xmlns='http://www.w3.org/2000/svg' class='d-inline'><path d='M1.41461 5.00006H1.42047M1.41461 1.48773H1.42047M1.41461 8.51239H1.42047M2 5.00006C2 5.32337 1.73792 5.58545 1.41461 5.58545C1.0913 5.58545 0.829224 5.32337 0.829224 5.00006C0.829224 4.67675 1.0913 4.41467 1.41461 4.41467C1.73792 4.41467 2 4.67675 2 5.00006ZM2 8.51239C2 8.8357 1.73792 9.09778 1.41461 9.09778C1.0913 9.09778 0.829224 8.8357 0.829224 8.51239C0.829224 8.18908 1.0913 7.927 1.41461 7.927C1.73792 7.927 2 8.18908 2 8.51239ZM2 1.48773C2 1.81103 1.73792 2.07312 1.41461 2.07312C1.0913 2.07312 0.829224 1.81103 0.829224 1.48773C0.829224 1.16443 1.0913 0.902344 1.41461 0.902344C1.73792 0.902344 2 1.16443 2 1.48773Z' stroke='#9E9E9E' stroke-width='1.17078' stroke-linecap='round' stroke-linejoin='round'/></svg>), você pode <b> editar </b> seu treino, marcar como <b> resolvido </b> e ver a <b> evolução de carga. </b></span></div>",
                scrollTo: "tooltip",
              },
              {
                title: getStepTitleWithCurrentStep(7),
                intro:
                  "<div class='w-100 text-center'><span class='d-block'><b> Parabéns! </b> <br /> Agora você está pronto(a) para elevar o nível da sua consultoria, ficando por dentro de tudo que acontece nos treinos dos seus alunos!</span></div>",
                scrollTo: "tooltip",
              },
            ]);

            this.refresh(true);
          });
        }
      });

      this.introJS.onexit(function () {
        $(".execution-datatable").css({
          overflow: "",
        });
        self.tableExpansionService.reset();

        self.isOnTour = false;

        localStorage.setItem(TOUR_KEY, "true");
      });
    }
  }

  loadindLifeCycleManager() {
    this.$isLoading.subscribe((isLoading) => {
      if (isLoading) {
        this.spinner.show(undefined, {
          type: "ball-triangle-path",
          size: "medium",
          bdColor: "rgba(0, 0, 0, 0.8)",
          color: "#fff",
          fullScreen: true,
        });
      } else {
        this.spinner.hide();
      }
    });
  }

  sendFeedback() {
    if (!this.feedback || this.feedbackLoading) return;

    this.feedbackLoading = true;
    this.apiCall
      .post("feature/feedback", {
        feature: "evolution-monitoring",
        rating:
          this.feedback === "positive"
            ? 3
            : this.feedback === "neutral"
            ? 2
            : 1,
        note: this.feedbackNote,
      })
      .subscribe((response) => {
        this.feedbackLoading = false;

        if (response.error) {
          this.toastr.error(
            "Ocorreu um erro ao enviar o feedback, tente novamente."
          );
        } else {
          this.toastr.success("Feedback enviado com sucesso.");
          this.feedback = null;
          this.feedbackNote = "";
          this.hasFeedback = true;
          this.ref.detectChanges();
        }
      });
  }

  isFiltered() {
    return Object.values(this.$filters.value).some((value) => value);
  }

  isFilterActive(filter: keyof ExecutionListFilters) {
    return this.$filters.value[filter];
  }

  getActiveFilters() {
    return this.filters;
  }

  getFilterLabel(filter: keyof ExecutionListFilters) {
    switch (filter) {
      case "lightFeedback":
        return "Feedback leve";
      case "heavyFeedback":
        return "Feedback pesado";
      case "withObservation":
        return "Com observação";
      case "withLoadNotification":
        return "Evolução de Carga";
    }
  }

  toggleFilter(filter: keyof ExecutionListFilters) {
    this.$filters.next({
      ...this.$filters.value,
      [filter]: !this.$filters.value[filter],
    });

    if (this.$filters.value[filter]) {
      this.filters.push(filter);
    } else {
      this.filters = this.filters.filter((f) => f !== filter);
    }
  }

  unsetFilter(filter: keyof ExecutionListFilters) {
    this.$filters.next({
      ...this.$filters.value,
      [filter]: false,
    });
    this.filters = this.filters.filter((f) => f !== filter);

    this.refetchEvolutions();
  }

  applyFilters() {
    this.refetchEvolutions();
  }

  getExpanded() {
    return this.tableExpansionService.expanded;
  }

  toggleRowExpansion(id: number) {
    this.tableExpansionService.toggleRowExpansion(id);
  }

  isRowExpanded(id: number) {
    return this.tableExpansionService.isRowExpanded(id);
  }

  setFeedback(feedback: "negative" | "neutral" | "positive") {
    this.feedback = feedback;
  }

  resetSearch() {
    this.$search.next("");
  }
}
